import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/Button';
import { Dialog } from '../../../components/ui/Dialog';
import SearchInput from '../../../components/ui/SearchInput';
import { toast } from '../../../components/ui/notify';
import { usePermissions } from '../../../context/PermissionsContext';
import { encodeData } from '../../../utils/encodeAndEncode';
import {
  attachCoursesToClassroom,
  getClassroomCourses,
  getEligibleClassroomCourses,
  removeCourseFromClassroom,
} from '../../../services/organization/classroom/classroom.service';

const categoriesFor = (course) => {
  if (Array.isArray(course.course_category)) return course.course_category;
  try {
    const parsed = JSON.parse(course.course_category || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export default function ClassroomCourses({ classroomId }) {
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const [courses, setCourses] = useState([]);
  const [eligible, setEligible] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isAttaching, setIsAttaching] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const searchTimer = useRef(null);

  const loadCourses = async () => {
    setLoading(true);
    try {
      const response = await getClassroomCourses(classroomId);
      setCourses(response?.data?.courses || []);
    } catch {
      toast.error('Unable to load classroom courses');
    } finally {
      setLoading(false);
    }
  };

  const loadEligible = async (value = '') => {
    try {
      const response = await getEligibleClassroomCourses(classroomId, value);
      setEligible(response?.data?.courses || []);
    } catch {
      toast.error('Unable to load available classroom courses');
    }
  };

  useEffect(() => { loadCourses(); }, [classroomId]);
  useEffect(() => {
    if (!showPicker) return undefined;
    window.clearTimeout(searchTimer.current);
    searchTimer.current = window.setTimeout(() => loadEligible(search), 250);
    return () => window.clearTimeout(searchTimer.current);
  }, [showPicker, search, classroomId]);

  const confirmAttach = async () => {
    setConfirmDialog(null);
    setIsAttaching(true);
    try {
      await attachCoursesToClassroom(classroomId, selected);
      toast.success(`${selected.length} course${selected.length === 1 ? '' : 's'} attached`);
      setSelected([]);
      setSearch('');
      setShowPicker(false);
      await loadCourses();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Unable to attach courses');
    } finally {
      setIsAttaching(false);
    }
  };

  const confirmRemove = async () => {
    const courseId = confirmDialog?.courseId;
    setConfirmDialog(null);
    if (!courseId) return;
    try {
      await removeCourseFromClassroom(classroomId, courseId);
      toast.success('Course removed from this classroom');
      await loadCourses();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Unable to remove course');
    }
  };

  const toggleCourse = (courseId) => {
    setSelected((current) => current.includes(courseId)
      ? current.filter((id) => id !== courseId)
      : [...current, courseId]);
  };

  const closePicker = () => {
    setShowPicker(false);
    setSelected([]);
    setSearch('');
  };

  return (
    <section className="tw-p-4 md:tw-p-6">
      <header className="tw-mb-5 tw-flex tw-flex-col tw-gap-3 tw-border-b tw-border-slate-200 tw-pb-5 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between">
        <div>
          <h5 className="tw-mb-1 tw-text-slate-900">Classroom courses</h5>
          <p className="tw-mb-0 tw-text-sm tw-text-slate-500">Only active members of this classroom can access these courses.</p>
        </div>
        <Button icon="fluent:add-24-regular" onClick={() => setShowPicker(true)}>
          Attach courses
        </Button>
      </header>

      {loading ? (
        <div className="tw-py-12 tw-text-center tw-text-slate-500">Loading courses…</div>
      ) : courses.length === 0 ? (
        <div className="tw-rounded-xl tw-border tw-border-dashed tw-border-slate-300 tw-bg-slate-50 tw-px-5 tw-py-12 tw-text-center">
          <Icon icon="fluent:book-open-24-regular" className="tw-mx-auto tw-mb-3 tw-text-4xl tw-text-slate-400" />
          <h6 className="tw-mb-1">No courses in this classroom</h6>
          <p className="tw-mb-0 tw-text-sm tw-text-slate-500">Attach a classroom-only course to make it available to members.</p>
        </div>
      ) : (
        <div className="tw-overflow-hidden tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white">
          <div className="tw-border-b tw-border-slate-200 tw-bg-slate-50 tw-px-5 tw-py-4">
            <h6 className="tw-mb-0">{courses.length} attached course{courses.length === 1 ? '' : 's'}</h6>
          </div>
          <div className="tw-hidden tw-grid-cols-[minmax(0,1fr)_minmax(150px,.55fr)_auto] tw-gap-4 tw-border-b tw-border-slate-200 tw-bg-slate-50/60 tw-px-5 tw-py-3 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500 md:tw-grid">
            <span>Course title</span>
            <span>Category</span>
            <span className="tw-text-right">Actions</span>
          </div>
          {courses.map((course) => (
            <article key={course.id} className="tw-grid tw-grid-cols-1 tw-items-center tw-gap-3 tw-border-b tw-border-slate-100 tw-px-5 tw-py-4 last:tw-border-b-0 md:tw-grid-cols-[minmax(0,1fr)_minmax(150px,.55fr)_auto] md:tw-gap-4">
              <div className="tw-min-w-0">
                <span className="tw-mb-1 tw-block tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-400 md:tw-hidden">Course title</span>
                <h6 className="tw-mb-0 tw-truncate tw-text-slate-900" title={course.course_name}>{course.course_name}</h6>
              </div>
              <div className="tw-min-w-0">
                <span className="tw-mb-1 tw-block tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-400 md:tw-hidden">Category</span>
                <p className="tw-mb-0 tw-truncate tw-text-sm tw-text-slate-600" title={categoriesFor(course).join(', ')}>{categoriesFor(course).join(', ') || 'Uncategorized'}</p>
              </div>
              <div className="tw-flex tw-flex-wrap tw-gap-2 md:tw-justify-end">
                <Button variant="outline-secondary" size="sm" icon="fluent:open-24-regular" onClick={() => navigate(`/admin/courses/module/${encodeData(course.id)}`)}>
                  Open course
                </Button>
                <Button
                  variant="outline-info"
                  size="sm"
                  icon="tabler:chart-bar"
                  title={permissions.course_management_view_analytics ? 'Open course analytics' : "You don't have permission to view analytics"}
                  disabled={!permissions.course_management_view_analytics}
                  onClick={() => navigate(`/admin/courses/analytics/${encodeData(course.id)}`)}
                >
                  Analytics
                </Button>
                <Button variant="outline-danger" size="sm" icon="fluent:delete-24-regular" onClick={() => setConfirmDialog({ type: 'remove', courseId: course.id })}>
                  Remove
                </Button>
              </div>
            </article>
          ))}
        </div>
      )}

      <Dialog show={showPicker} onHide={closePicker} size="lg" centered scrollable>
        <Dialog.Header closeButton>
          <div>
            <Dialog.Title>Attach courses</Dialog.Title>
            <p className="tw-mb-0 tw-mt-1 tw-text-sm tw-text-slate-500">Only approved classroom-only courses that are not already attached are shown.</p>
          </div>
        </Dialog.Header>
        <Dialog.Body className="tw-p-0">
          <div className="tw-border-b tw-border-slate-200 tw-p-5">
              <SearchInput placeholder="Search courses…" value={search} onChange={(event) => setSearch(event.target.value)} />
          </div>
          <div className="tw-space-y-3 tw-p-5">
              {eligible.length === 0 ? (
                <div className="tw-py-10 tw-text-center tw-text-sm tw-text-slate-500">
                  {search ? 'No matching classroom-only courses found.' : 'No additional classroom-only courses are available to attach.'}
                </div>
              ) : eligible.map((course) => {
                const checked = selected.includes(course.id);
                return (
                  <label key={course.id} className={`tw-grid tw-cursor-pointer tw-grid-cols-[auto_minmax(0,1fr)] tw-items-center tw-gap-3 tw-rounded-xl tw-border tw-p-4 sm:tw-grid-cols-[auto_minmax(0,1fr)_minmax(150px,.55fr)] ${checked ? 'tw-border-indigo-500 tw-bg-indigo-50' : 'tw-border-slate-200 tw-bg-white hover:tw-bg-slate-50'}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggleCourse(course.id)} className="tw-h-5 tw-w-5" />
                    <h6 className="tw-mb-0 tw-min-w-0 tw-truncate" title={course.course_name}>{course.course_name}</h6>
                    <p className="tw-col-start-2 tw-mb-0 tw-truncate tw-text-sm tw-text-slate-500 sm:tw-col-start-3" title={categoriesFor(course).join(', ')}>{categoriesFor(course).join(', ') || 'Uncategorized'}</p>
                  </label>
                );
              })}
          </div>
        </Dialog.Body>
        <Dialog.Footer className="tw-justify-between">
          <span className="tw-mr-auto tw-text-sm tw-text-slate-500">{selected.length} selected</span>
          <Button variant="secondary" onClick={closePicker}>Cancel</Button>
          <Button loading={isAttaching} disabled={selected.length === 0} onClick={() => setConfirmDialog({ type: 'attach' })}>Attach selected</Button>
        </Dialog.Footer>
      </Dialog>

      <Dialog show={Boolean(confirmDialog)} onHide={() => setConfirmDialog(null)} centered>
        <Dialog.Header closeButton>
          <Dialog.Title>{confirmDialog?.type === 'attach' ? 'Attach selected courses?' : 'Remove course from this classroom?'}</Dialog.Title>
        </Dialog.Header>
        <Dialog.Body>
          <p className="tw-mb-0 tw-text-sm tw-leading-6 tw-text-slate-600">
            {confirmDialog?.type === 'attach'
              ? 'Members of this classroom will be able to discover and enroll in the selected courses.'
              : 'Learners who have no other linked classroom will immediately lose access. Their enrollment, purchase, and progress records will be kept.'}
          </p>
        </Dialog.Body>
        <Dialog.Footer>
          <Button variant="secondary" onClick={() => setConfirmDialog(null)}>Cancel</Button>
          <Button variant={confirmDialog?.type === 'attach' ? 'primary' : 'danger'} onClick={confirmDialog?.type === 'attach' ? confirmAttach : confirmRemove}>
            {confirmDialog?.type === 'attach' ? 'Attach courses' : 'Remove course'}
          </Button>
        </Dialog.Footer>
      </Dialog>
    </section>
  );
}
